import React from "react";
import { Table } from "antd";
import Typography from "antd/lib/typography/Typography";
import { LoadingOutlined } from "@ant-design/icons";
import "./style.css";

const { Title, Text } = Typography;
const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

const renderValue = (value, type = "text") => {
  if (type === "numeric" || type === "number") {
    if (typeof value === "number") return value.toLocaleString("id-ID");
    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? "-" : parsed.toLocaleString("id-ID");
  }
  if (type === "checkbox") {
    return <input type="checkbox" checked={!!value} disabled />;
  }
  return value ?? "-";
};

const TableChartComponent = ({
  title,
  tableDataSource = {},
  spinning = false,
  rowKey = "id",
}) => {
  const safeTableData = {
    column: Array.isArray(tableDataSource.column)
      ? [...tableDataSource.column]
      : [],
    rowData: Array.isArray(tableDataSource.rowData)
      ? [...tableDataSource.rowData]
      : [],
    summary: tableDataSource.summary || null,
  };

  const { column, rowData, summary } = safeTableData;

  // build columns & grouped columns seperti sebelumnya,
  // tapi juga simpan `component` pada setiap leaf column
  const columns = [];
  const groupedColumns = {};

  column.forEach((col) => {
    const renderedCol = {
      title: col.label,
      dataIndex: col.key,
      key: col.key,
      component: col.component,
      render: (text) => renderValue(text, col.component),
    };

    if (col.group_label) {
      if (!groupedColumns[col.group_label])
        groupedColumns[col.group_label] = [];
      groupedColumns[col.group_label].push(renderedCol);
    } else {
      columns.push(renderedCol);
    }
  });

  for (const [groupName, children] of Object.entries(groupedColumns)) {
    columns.push({
      title: groupName,
      children,
    });
  }

  // Flatten columns ke leaf columns (urutan tampil)
  const flattenLeafColumns = (cols) => {
    const leaves = [];
    cols.forEach((c) => {
      if (c.children && Array.isArray(c.children)) {
        c.children.forEach((child) => leaves.push(child));
      } else {
        leaves.push(c);
      }
    });
    return leaves;
  };

  const leafColumns = flattenLeafColumns(columns);

  // Summary renderer: dukung banyak summary.data entries
  const summaryRenderer = () => {
    if (!summary) return null;

    const summaryItems = Array.isArray(summary.data) ? summary.data : [];
    const summaryMap = {};
    summaryItems.forEach((it) => {
      if (it && it.key !== undefined) summaryMap[it.key] = it.value;
    });

    return (
      <Table.Summary.Row>
        {leafColumns.map((col, idx) => {
          // kolom paling kiri -> label summary
          if (idx === 0) {
            return (
              <Table.Summary.Cell key={`summary-label`}>
                <strong>{summary.label}</strong>
              </Table.Summary.Cell>
            );
          }

          const colKey = col.dataIndex || col.key;
          if (summaryMap.hasOwnProperty(colKey)) {
            const rawValue = summaryMap[colKey];

            // deteksi persentase dari key/title
            const isPercent =
              /persentase|persen|%/i.test(String(colKey)) ||
              /persentase|persen|%/i.test(String(col.title));

            let display;
            if (isPercent && typeof rawValue === "number") {
              display =
                rawValue * 100 === 0
                  ? "0 %"
                  : `${(rawValue * 100).toLocaleString("id-ID", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })} %`;
            } else {
              display = renderValue(rawValue, col.component);
            }

            return (
              <Table.Summary.Cell key={`summary-${colKey}`}>
                <strong>{display}</strong>
              </Table.Summary.Cell>
            );
          }

          // tidak ada nilai summary untuk kolom ini
          return <Table.Summary.Cell key={`summary-empty-${colKey}`} />;
        })}
      </Table.Summary.Row>
    );
  };

  return (
    <div className="custom-card-container">
      <div className="card-title-responsive">{title}</div>
      <div className="table-container">
        <Table
          rowKey={rowKey}
          columns={columns}
          dataSource={rowData}
          loading={{
            spinning,
            indicator: antIcon,
          }}
          scroll={{ x: "100%", y: 500 }}
          pagination={false}
          bordered
          summary={summaryRenderer}
        />
      </div>
    </div>
  );
};

export default TableChartComponent;
